import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, X, AlertTriangle } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal de Troca de Operador (Troca de Turno do PDV).
 * Permite que o próximo operador assuma o caixa sem deslogar/logar manualmente.
 * Pede email + senha do próximo operador e troca o cookie de sessão.
 */
export const SwitchOperatorModal = ({ open, onClose, currentUser }) => {
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  if (!open) return null;

  const handleSwitch = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      await axios.post(`${API}/auth/switch-operator`, { email, password }, { withCredentials: true });
      // Recarrega contexto de autenticação para refletir o novo usuário
      if (refresh) await refresh();
      // Limpa carrinho do operador anterior e força reload da página (PDV recarrega tudo)
      try {
        const oldKey = `sf_pdv_cart_${currentUser?.id}`;
        if (oldKey) localStorage.removeItem(oldKey);
      } catch (_) {}
      window.location.reload();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha na troca. Verifique email e senha.');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="switch-operator-modal">
        <div className="bg-amber-500 text-white px-5 py-3 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus size={20} />
            <h3 className="font-bold text-lg">Troca de Operador</h3>
          </div>
          <button onClick={onClose} className="hover:bg-amber-600 rounded p-1" data-testid="switch-close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSwitch} className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 flex gap-2 text-sm">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={16} />
            <div>
              <p className="font-bold text-amber-800">Atenção</p>
              <p className="text-amber-700">
                O caixa atual está sendo operado por <strong>{currentUser?.name}</strong>.
                Após a troca, o próximo operador assume o controle <strong>do mesmo caixa aberto</strong>.
                As vendas continuarão somando neste turno até o fechamento.
              </p>
              <p className="text-amber-700 mt-1">
                💡 Se você prefere caixas separados, peça que o operador atual <strong>feche o caixa</strong> antes.
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Email do próximo operador *</Label>
            <Input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="operador@email.com"
              data-testid="switch-email"
            />
          </div>
          <div className="space-y-1">
            <Label>Senha *</Label>
            <Input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha do próximo operador"
              data-testid="switch-password"
            />
          </div>

          {err && <div className="bg-red-50 border border-red-300 text-red-700 px-3 py-2 rounded text-sm" data-testid="switch-error">{err}</div>}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" data-testid="switch-cancel">
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !email || !password}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="switch-confirm"
            >
              {loading ? 'Trocando...' : 'Confirmar Troca'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
